import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { achtungKommentar } from '../lib/achtung';
import { api } from '../lib/api';
import { StandCell } from '../components/StandCell';
import { HeuteModulWidget } from '../components/HeuteWidget';
import { AbteilungBadge } from '../components/AbteilungBadge';
import BeistelteileIndicator, { useBeistelteileSummary } from '../components/BeistelteileIndicator';
import { VorkasseLockBadge, isVorkasseGesperrt } from '../components/VorkasseBanner';

// Tag-Offset → YYYY-MM-DD (lokale Zeit)
function isoDate(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

type Bucket = 'ueberfaellig' | 'kritisch' | 'bald' | 'naechste_woche';

const bucketLabels: Record<Bucket, string> = {
  ueberfaellig:   'Überfällige Aufträge',
  kritisch:       'Kritisch (heute bis 3 Tage)',
  bald:           'Bald (4–7 Tage)',
  naechste_woche: 'Nächste Woche (8–14 Tage)',
};

function bucketToFilters(bucket: Bucket): Partial<ColFilters> {
  switch (bucket) {
    case 'ueberfaellig':   return { datum_to: isoDate(-1) };
    case 'kritisch':       return { datum_from: isoDate(0),  datum_to: isoDate(3) };
    case 'bald':           return { datum_from: isoDate(4),  datum_to: isoDate(7) };
    case 'naechste_woche': return { datum_from: isoDate(8),  datum_to: isoDate(14) };
  }
}

type SortDir = 'asc' | 'desc';
type SortKey = 'bestellnr' | 'dv_nummer' | 'position' | 'kunde' | 'bezeichnung'
             | 'stueck' | 'lieferdatum' | 'kw' | 'gesamtzeit' | 'stand' | 'sb' | 'priority';

interface ColFilters {
  bestellnr_like:   string;
  dv_nummer_like:   string;
  position_like:    string;
  kunde_id:         string;
  bezeichnung_like: string;
  stueck_min:       string;
  datum_from:       string;
  datum_to:         string;
  kw:               string;
  zeit_min:         string;
  stand_id:         string;
  sachbearbeiter_id: string;
  priority:         string;
}

const emptyFilters: ColFilters = {
  bestellnr_like: '', dv_nummer_like: '', position_like: '', kunde_id: '',
  bezeichnung_like: '', stueck_min: '', datum_from: '', datum_to: '', kw: '',
  zeit_min: '', stand_id: '', sachbearbeiter_id: '', priority: ''
};

export function Uebersicht() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ColFilters>(emptyFilters);
  const [fertigFilter, setFertigFilter] = useState<'offen' | 'fertig' | 'alle'>('offen');
  const [sort, setSort] = useState<SortKey>('lieferdatum');
  const [dir,  setDir]  = useState<SortDir>('asc');
  const [offset, setOffset] = useState(0);
  const [activeBucket, setActiveBucket] = useState<Bucket | null>(null);

  // URL-Parameter lesen (z.B. ?bucket=ueberfaellig vom Dashboard)
  useEffect(() => {
    const bucket = searchParams.get('bucket') as Bucket | null;
    if (bucket && bucketLabels[bucket]) {
      const bf = bucketToFilters(bucket);
      setFilters({ ...emptyFilters, ...bf });
      setFertigFilter('offen');
      setSort('lieferdatum');
      setDir('asc');
      setOffset(0);
      setActiveBucket(bucket);
      // URL temiz kalsın: paramı kaldır (filtre state'te tutulur)
      searchParams.delete('bucket');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kunden     = useApi(() => api.lookup.kunden(), []);
  const users      = useApi(() => api.lookup.users(), []);
  const standWerte = useApi(() => api.lookup.standWerte(), []);

  const params: Record<string, string | number | undefined> = {
    q: search || undefined,
    limit: 50,
    offset,
    sort, dir,
    fertig: fertigFilter === 'offen' ? 0 : fertigFilter === 'fertig' ? 1 : undefined,
  };
  (Object.keys(filters) as Array<keyof ColFilters>).forEach(k => {
    const v = filters[k];
    if (v !== '' && v !== undefined) params[k] = v;
  });

  const list = useApi(
    () => api.drueckteile.list(params),
    [search, JSON.stringify(filters), fertigFilter, sort, dir, offset]
  );

  const beistelteileSummary = useBeistelteileSummary(
    'drueckteile',
    list.data?.data.map(d => d.id) ?? []
  );

  const setFilter = <K extends keyof ColFilters>(k: K, v: ColFilters[K]) => {
    setFilters(p => ({ ...p, [k]: v }));
    setOffset(0);
  };

  const toggleSort = (key: SortKey) => {
    if (sort === key) {
      setDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(key);
      setDir('asc');
    }
    setOffset(0);
  };

  const resetAll = () => {
    setFilters(emptyFilters);
    setSearch('');
    setFertigFilter('offen');
    setSort('lieferdatum'); setDir('asc'); setOffset(0);
    setActiveBucket(null);
  };

  const hasActiveFilters =
    search !== '' ||
    (Object.values(filters) as string[]).some(v => v !== '') ||
    fertigFilter !== 'offen';

  return (
    <div className="p-8">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Produktion</div>
          <h1 className="text-2xl font-bold text-slate-900">Drückteile – Übersicht</h1>
        </div>
        <div className="flex gap-2">
          {hasActiveFilters && (
            <button onClick={resetAll} className="btn-secondary text-sm">
              Filter zurücksetzen
            </button>
          )}
          <Link to="/drueckteile/neu" className="btn-primary">+ Neuer Auftrag</Link>
        </div>
      </header>

      <HeuteModulWidget modul="drueckteile" label="Drückteile" />

      {activeBucket && (
        <div className={`border rounded-xl p-3 mb-4 flex items-center justify-between
                        ${activeBucket === 'ueberfaellig' ? 'bg-red-50 border-red-200 text-red-800' :
                          activeBucket === 'kritisch'     ? 'bg-orange-50 border-orange-200 text-orange-800' :
                          activeBucket === 'bald'         ? 'bg-amber-50 border-amber-200 text-amber-800' :
                                                            'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
          <div className="text-sm font-medium">
            Filter aktiv: <strong>{bucketLabels[activeBucket]}</strong>
          </div>
          <button onClick={resetAll} className="text-xs underline hover:no-underline">
            Filter entfernen
          </button>
        </div>
      )}

      {/* Globale Suche + Status-Toggle */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4 flex items-center gap-3">
        <input type="search"
               placeholder="🔍 Globale Suche (DV-Nr, Bestellnr, Bezeichnung, Kunde) ..."
               value={search} onChange={e => { setSearch(e.target.value); setOffset(0); }}
               className="flex-1 input" />
        <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5">
          {(['offen','fertig','alle'] as const).map(v => (
            <button key={v} onClick={() => { setFertigFilter(v); setOffset(0); }}
                    className={`px-3 py-1.5 rounded text-sm font-medium ${
                      fertigFilter === v ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}>
              {v === 'offen' ? 'Offen' : v === 'fertig' ? 'Fertig' : 'Alle'}
            </button>
          ))}
        </div>
        <div className="text-sm text-slate-500 whitespace-nowrap px-2">
          {list.data ? `${list.data.pagination.total} Ergebnisse` : '…'}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-[13px] min-w-[1120px]">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            {/* Sort headers */}
            <tr className="text-xs text-slate-600 uppercase">
              <Th sortKey="bestellnr"    label="Bestellnr"    sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="dv_nummer"    label="DV-Nr"        sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="position"     label="Pos"          sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="kunde"        label="Kunde"        sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="bezeichnung"  label="Bezeichnung"  sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="stueck"       label="Stk"          sort={sort} dir={dir} onClick={toggleSort} align="right" />
              <Th sortKey="lieferdatum"  label="Liefertermin" sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="kw"           label="KW"           sort={sort} dir={dir} onClick={toggleSort} align="center" />
              <Th sortKey="gesamtzeit"   label="Zeit"         sort={sort} dir={dir} onClick={toggleSort} align="right" />
              <Th sortKey="stand"        label="Stand"        sort={sort} dir={dir} onClick={toggleSort} />
              <th className="px-2.5 py-1.5 font-medium text-left">Aktuell</th>
              <Th sortKey="sb"           label="SB"           sort={sort} dir={dir} onClick={toggleSort} />
              <Th sortKey="priority"     label="Prio"         sort={sort} dir={dir} onClick={toggleSort} />
            </tr>
            {/* Filter row */}
            <tr className="bg-slate-50 border-b border-slate-200">
              <Filter><input className="colf" placeholder="…" value={filters.bestellnr_like}
                             onChange={e => setFilter('bestellnr_like', e.target.value)} /></Filter>
              <Filter><input className="colf" placeholder="…" value={filters.dv_nummer_like}
                             onChange={e => setFilter('dv_nummer_like', e.target.value)} /></Filter>
              <Filter><input className="colf" placeholder="…" value={filters.position_like}
                             onChange={e => setFilter('position_like', e.target.value)} /></Filter>
              <Filter>
                <select className="colf" value={filters.kunde_id}
                        onChange={e => setFilter('kunde_id', e.target.value)}>
                  <option value="">Alle</option>
                  {kunden.data?.data.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                </select>
              </Filter>
              <Filter><input className="colf" placeholder="Maße / Bez." value={filters.bezeichnung_like}
                             onChange={e => setFilter('bezeichnung_like', e.target.value)} /></Filter>
              <Filter><input type="number" className="colf" placeholder="≥" value={filters.stueck_min}
                             onChange={e => setFilter('stueck_min', e.target.value)} /></Filter>
              <Filter>
                <div className="flex gap-1">
                  <input type="date" className="colf" value={filters.datum_from}
                         onChange={e => setFilter('datum_from', e.target.value)} title="Von" />
                  <input type="date" className="colf" value={filters.datum_to}
                         onChange={e => setFilter('datum_to', e.target.value)} title="Bis" />
                </div>
              </Filter>
              <Filter><input type="number" className="colf" placeholder="KW" value={filters.kw}
                             onChange={e => setFilter('kw', e.target.value)} /></Filter>
              <Filter><input type="number" step="0.25" className="colf" placeholder="≥ h" value={filters.zeit_min}
                             onChange={e => setFilter('zeit_min', e.target.value)} /></Filter>
              <Filter>
                <select className="colf" value={filters.stand_id}
                        onChange={e => setFilter('stand_id', e.target.value)}>
                  <option value="">Alle</option>
                  {standWerte.data?.data.map(s => <option key={s.id} value={s.id}>{s.wert}</option>)}
                </select>
              </Filter>
              <Filter>
                <select className="colf" value={filters.sachbearbeiter_id}
                        onChange={e => setFilter('sachbearbeiter_id', e.target.value)}>
                  <option value="">Alle</option>
                  {users.data?.data.filter(u => u.kurzel !== 'SYS').map(u =>
                    <option key={u.id} value={u.id}>{u.name}</option>
                  )}
                </select>
              </Filter>
              <td></td>{/* Aktuell Abteilung — no filter */}
              <Filter>
                <select className="colf" value={filters.priority}
                        onChange={e => setFilter('priority', e.target.value)}>
                  <option value="">Alle</option>
                  <option value="normal">Normal</option>
                  <option value="eilig">Eilig</option>
                  <option value="kritisch">Kritisch</option>
                </select>
              </Filter>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.loading && <tr><td colSpan={12} className="text-center py-12 text-slate-500">Lädt …</td></tr>}
            {list.data && list.data.data.length === 0 && (
              <tr><td colSpan={12} className="text-center py-12 text-slate-500">Keine Ergebnisse</td></tr>
            )}
            {list.data?.data.map(d => {
              const achtung = achtungKommentar(d.kommentar);
              const gesperrt = isVorkasseGesperrt(d.vorkasse, d.vorkasse_bezahlt_am);
              return (
              <tr key={d.id} className={gesperrt ? 'bg-slate-100 text-slate-500 hover:bg-slate-200' : achtung ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-slate-50'}>
                <td className="px-2.5 py-1.5 font-mono text-xs">
                  <div className="flex items-center gap-1">
                    {d.bestellnr}
                    <BeistelteileIndicator summary={beistelteileSummary[String(d.id)]} />
                    <VorkasseLockBadge vorkasse={d.vorkasse} bezahltAm={d.vorkasse_bezahlt_am} />
                  </div>
                </td>
                <td className="px-2.5 py-1.5 font-mono text-xs">
                  <Link to={`/drueckteile/${d.id}`} className="text-brand hover:underline">
                    {d.dv_nummer ?? '—'}
                  </Link>
                </td>
                <td className="px-2.5 py-1.5">{d.position ?? '—'}</td>
                <td className="px-2.5 py-1.5 max-w-[150px] truncate" title={d.kunde_name ?? ''}>{d.kunde_name ?? '—'}</td>
                <td className="px-2.5 py-1.5 max-w-[190px]" title={d.masse_bezeichnung ?? ''}>
                  <div className="truncate">{d.masse_bezeichnung ?? '—'}</div>
                  {d.kommentar && (achtung ? (
                    <div className="text-xs font-bold text-amber-700 whitespace-normal mt-0.5">⚠ {d.kommentar}</div>
                  ) : (
                    <div className="text-xs text-slate-500 whitespace-normal mt-0.5">💬 {d.kommentar}</div>
                  ))}
                </td>
                <td className="px-2.5 py-1.5 text-right">{d.stueck}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap"><LieferDatum iso={d.lieferdatum} fertig={d.fertig === 1} /></td>
                <td className="px-2.5 py-1.5 text-center">
                  <span className="bg-sky-100 text-sky-900 px-2 py-0.5 rounded text-xs font-semibold">{d.kw}</span>
                </td>
                <td className="px-2.5 py-1.5 text-right whitespace-nowrap">{d.gesamtzeit}h</td>
                <td className="px-2.5 py-1.5 min-w-[135px]">
                  <StandCell
                    drueckteilId={d.id}
                    currentStandId={d.stand_id}
                    currentStandWert={d.stand_wert}
                    currentStandFarbe={d.stand_farbe}
                    updatedAt={d.updated_at}
                    standWerte={standWerte.data?.data ?? []}
                    onChanged={() => list.reload()} />
                </td>
                <td className="px-2.5 py-1.5">
                  <AbteilungBadge session={d.current_session} />
                </td>
                <td className="px-2.5 py-1.5">
                  <span className="bg-slate-100 rounded px-2 py-0.5 text-xs">{d.sachbearbeiter_name ?? '—'}</span>
                </td>
                <td className="px-2.5 py-1.5">
                  {d.priority === 'eilig'    && <span className="text-xs font-bold text-red-600">EILIG</span>}
                  {d.priority === 'kritisch' && <span className="text-xs font-bold text-red-800">KRITISCH</span>}
                  {d.priority === 'normal'   && <span className="text-xs text-slate-400">—</span>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>

        {list.data && list.data.pagination.total > list.data.pagination.limit && (
          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between text-sm text-slate-600">
            <div>
              Zeige {offset + 1}–{Math.min(offset + list.data.pagination.limit, list.data.pagination.total)} von {list.data.pagination.total}
            </div>
            <div className="flex gap-2">
              <button className="btn-secondary" disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - 50))}>‹ Zurück</button>
              <button className="btn-secondary" disabled={!list.data.pagination.hasMore}
                      onClick={() => setOffset(offset + 50)}>Weiter ›</button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .colf {
          width: 100%;
          font-size: 12px;
          padding: 4px 8px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          background: white;
        }
        .colf:focus {
          outline: none;
          border-color: #0284C7;
          box-shadow: 0 0 0 2px rgba(2,132,199,0.1);
        }
      `}</style>
    </div>
  );
}

function Th({ label, sortKey, sort, dir, onClick, align = 'left' }: {
  label: string; sortKey: SortKey; sort: SortKey; dir: SortDir;
  onClick: (key: SortKey) => void; align?: 'left' | 'right' | 'center';
}) {
  const active = sort === sortKey;
  const arrow  = !active ? '↕' : dir === 'asc' ? '▲' : '▼';
  return (
    <th onClick={() => onClick(sortKey)}
        className={`px-2.5 py-1.5 font-medium cursor-pointer select-none hover:bg-slate-100 ${
          align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
        } ${active ? 'text-brand' : ''}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[10px] ${active ? 'text-brand' : 'text-slate-300'}`}>{arrow}</span>
      </span>
    </th>
  );
}

function Filter({ children }: { children: React.ReactNode }) {
  return <td className="px-2 py-1.5">{children}</td>;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function LieferDatum({ iso, fertig }: { iso: string; fertig: boolean }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(iso); d.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const formatted = formatDate(iso);
  if (fertig) return <span className="text-slate-400">{formatted}</span>;
  let cls = 'text-slate-700';
  let badge: string | null = null;
  if (days < 0)        { cls = 'text-red-700 font-semibold';    badge = `${Math.abs(days)}T verspätet`; }
  else if (days <= 3)  { cls = 'text-orange-700 font-semibold'; badge = days === 0 ? 'HEUTE' : days === 1 ? 'morgen' : `in ${days}T`; }
  else if (days <= 7)  { cls = 'text-amber-700'; }
  return (
    <span className={cls} title={badge ?? `in ${days} Tagen`}>
      {formatted}{badge && <span className="ml-1 text-[10px] opacity-75">· {badge}</span>}
    </span>
  );
}
